import React, { createContext, useContext } from 'react';
import { useSelector } from 'react-redux';
import { STAFF_PERMISSIONS } from '../constants/permissions';

const PermissionContext = createContext(null);

export const PermissionProvider = ({ children }) => {
  const { admin } = useSelector((state) => state.auth);

  // If no admin user, no permissions
  const getPermissions = () => {
    if (!admin) {
      return {
        can: () => false,
        canAny: () => false,
        canAll: () => false,
        isPlatformAdmin: false,
        isStaff: false,
        hasPermission: () => false,
        getAllPermissions: () => ({}),
        getStaffRole: () => null,
      };
    }

    // Platform admin has all permissions
    if (admin.isPlatformAdmin) {
      return {
        can: () => true,
        canAny: () => true,
        canAll: () => true,
        isPlatformAdmin: true,
        isStaff: false,
        hasPermission: () => true,
        getAllPermissions: () => Object.values(STAFF_PERMISSIONS).reduce(
          (acc, key) => ({ ...acc, [key]: true }),
          {}
        ),
        getStaffRole: () => 'platform_admin',
      };
    }

    // Staff member - check specific permissions
    const permissions = admin.permissions || {};

    return {
      can: (permissionKey) => permissions[permissionKey] === true,
      
      canAny: (...permissionKeys) => {
        return permissionKeys.some(key => permissions[key] === true);
      },
      
      canAll: (...permissionKeys) => {
        return permissionKeys.every(key => permissions[key] === true);
      },
      
      isPlatformAdmin: false,
      isStaff: !!admin.staffRole,
      hasPermission: (permissionKey) => permissions[permissionKey] === true,
      getAllPermissions: () => permissions,
      getStaffRole: () => admin.staffRole,
    };
  };

  return (
    <PermissionContext.Provider value={getPermissions()}>
      {children}
    </PermissionContext.Provider>
  );
};

// Hook to use permissions
export const usePermissions = () => {
  const context = useContext(PermissionContext);
  
  if (context === null) {
    throw new Error('usePermissions must be used within PermissionProvider');
  }
  
  return context;
};